/**
 * InfoBoxx — Frequently Asked Questions
 * Organised by topic → category (Top 30) → Q&A.
 */

const GOLD = "#FFD221";

/** @param {[string, string][]} rows */
const pack = (rows) => rows.map(([q, a]) => ({ q, a }));

const REAL_ESTATE_ROWS = [
  ["How do I buy land safely?", "Always verify ownership, check documents at the Lands Commission, and work with a trusted agent or lawyer."],
  ["What documents should I check before buying land?", "Check the land title, site plan, indenture, and ownership history."],
  ["What is a land title certificate?", "It is proof that the land legally belongs to the seller."],
  ["What is the difference between leasehold and freehold?", "Freehold means full ownership; leasehold means you own it for a fixed number of years."],
  ["Can I buy land without a lawyer?", "It is possible, but not recommended. A lawyer helps avoid fraud."],
  ["How much does it cost to register land?", "Costs vary, but include legal fees, stamp duty, and registration fees."],
  ["How long does land registration take?", "It can take a few months to over a year depending on the process."],
  ["What is a site plan?", "A drawing that shows the exact location and boundaries of the land."],
  ["What is land litigation?", "It means the land is involved in a legal dispute."],
  ["How do I avoid buying disputed land?", "Conduct proper checks and consult the Lands Commission."],
  ["What is a mortgage?", "A loan used to buy property, which you repay over time."],
  ["How do I qualify for a mortgage?", "You need stable income, good credit, and ability to pay monthly installments."],
  ["How much deposit do I need for a mortgage?", "Usually between 10% to 30% of the property price."],
  ["What is interest rate on a mortgage?", "It is the extra amount you pay on top of the loan."],
  ["How long can I repay a mortgage?", "Typically between 10 to 25 years."],
  ["Can I buy a house without full payment?", "Yes, through a mortgage or installment plan."],
  ["What is property valuation?", "It is determining how much a property is worth."],
  ["How do I find affordable housing?", "Search online, use agents, or check housing developers."],
  ["What is a real estate agent?", "A person who helps you buy, sell, or rent property."],
  ["Should I use a real estate agent?", "Yes, but ensure they are trusted and experienced."],
  ["What is rent advance?", "Payment made upfront before moving into a rented property."],
  ["How much rent advance is allowed?", "It depends on local laws, often 6 months to 2 years."],
  ["What is a tenancy agreement?", "A contract between landlord and tenant."],
  ["Can a landlord increase rent anytime?", "No, they must follow legal guidelines and agreements."],
  ["What should I check before renting a house?", "Check water, electricity, security, and location."],
  ["What is property insurance?", "Insurance that protects your property from damage or loss."],
  ["Can foreigners buy property?", "Yes, but usually under leasehold agreements."],
  ["What is land banking?", "Buying land and holding it for future value increase."],
  ["How do I sell my property fast?", "Price it well, market it online, and use agents."],
  ["What are the risks in real estate?", "Fraud, disputes, hidden costs, and poor documentation."],
];

const LEGAL_ROWS = [
  ["How do I register a business?", "Choose a name, register it with the Registrar, and get necessary licenses."],
  ["How long does business registration take?", "Usually a few days to a few weeks."],
  ["What documents are needed to register a business?", "ID, business name, address, and nature of business."],
  ["What is a contract?", "A legal agreement between two or more people."],
  ["Is a verbal agreement valid?", "Sometimes, but written agreements are safer."],
  ["What happens if someone breaks a contract?", "You can take legal action or seek compensation."],
  ["How do I file for divorce?", "Submit a petition through a lawyer in court."],
  ["How long does divorce take?", "It can take months depending on complexity."],
  ["Who gets child custody after divorce?", "The court decides based on the child’s best interest."],
  ["What is alimony?", "Financial support paid to a spouse after divorce."],
  ["What is bail?", "Money paid to release someone from police custody temporarily."],
  ["Can I represent myself in court?", "Yes, but having a lawyer is better."],
  ["What should I do if I am arrested?", "Stay calm, remain silent, and ask for a lawyer."],
  ["What is a will?", "A document stating how your property is shared after death."],
  ["Do I need a lawyer to write a will?", "Not required, but recommended."],
  ["What happens if someone dies without a will?", "The law decides how assets are shared."],
  ["What is probate?", "The legal process of validating a will."],
  ["How do I recover debt legally?", "Through demand letters or court action."],
  ["What is bankruptcy?", "When someone cannot repay debts and seeks legal protection."],
  ["What are my rights as an employee?", "Fair pay, safe conditions, and protection from unfair dismissal."],
  ["Can I sue my employer?", "Yes, if your rights are violated."],
  ["What is wrongful termination?", "Being fired unfairly or illegally."],
  ["How do I apply for a visa legally?", "Submit documents through the embassy or official channels."],
  ["What is a work permit?", "Permission to work in another country."],
  ["What is intellectual property?", "Ownership of ideas, designs, or creations."],
  ["How do I protect my business idea?", "Register trademarks or copyrights."],
  ["What is a power of attorney?", "A document allowing someone to act on your behalf."],
  ["Can I settle disputes without court?", "Yes, through mediation or arbitration."],
  ["What is fraud?", "Deceiving someone for personal gain."],
  ["When should I contact a lawyer?", "Whenever you face legal risk, confusion, or important decisions."],
];

const NUTRITION_ROWS = [
  ["What is a healthy meal plan?", "A healthy meal plan includes balanced portions of proteins, carbohydrates, fats, fruits, and vegetables."],
  ["How can I lose weight through food?", "Eat fewer calories, avoid junk food, and focus on vegetables, lean protein, and water."],
  ["How many meals should I eat in a day?", "Most people eat 2–3 main meals and 1–2 small snacks."],
  ["What foods help with weight loss?", "Vegetables, fruits, lean meat, fish, eggs, and whole grains."],
  ["What foods should I avoid for better health?", "Sugary drinks, fried foods, processed snacks, and excess salt."],
  ["Can I lose weight without exercise?", "Yes, but combining good diet with exercise gives better results."],
  ["What is a balanced diet?", "Eating different types of food in the right amounts."],
  ["How much water should I drink daily?", "About 6–8 glasses per day, or more if you are active."],
  ["What is meal prepping?", "Preparing meals in advance to save time and eat healthier."],
  ["What are cheap healthy meals?", "Meals like rice and vegetables, beans, eggs, and local soups."],
  ["What is a diet plan?", "A guide that shows what to eat daily for a specific goal."],
  ["How can I gain weight healthily?", "Eat more calories from healthy foods like rice, proteins, and nuts."],
  ["What foods help build muscle?", "Protein-rich foods like eggs, chicken, fish, beans, and milk."],
  ["What is a low-carb diet?", "A diet with fewer carbohydrates like rice and bread."],
  ["What is a detox diet?", "A short-term plan focused on fruits, vegetables, and fluids to cleanse the body."],
  ["Are local foods healthy?", "Yes, many local foods are natural and nutritious when prepared well."],
  ["What is a vegan diet?", "A diet that excludes all animal products."],
  ["What is a vegetarian diet?", "A diet without meat but may include eggs and dairy."],
  ["What foods are good for diabetics?", "Low-sugar foods like vegetables, whole grains, and lean protein."],
  ["What is portion control?", "Eating the right amount of food, not too much or too little."],
  ["Can I eat at night and still be healthy?", "Yes, but keep meals light and avoid heavy foods late at night."],
  ["What are healthy snacks?", "Fruits, nuts, yogurt, and boiled eggs."],
  ["How do I plan meals for the week?", "Choose meals in advance, shop once, and prepare early."],
  ["What is calorie counting?", "Tracking how much energy you get from food."],
  ["What foods improve energy levels?", "Whole grains, fruits, vegetables, and protein-rich foods."],
  ["What foods boost immunity?", "Fruits, vegetables, garlic, ginger, and vitamin-rich foods."],
  ["Can children follow meal plans?", "Yes, but meals should support growth and include all nutrients."],
  ["What is intermittent fasting?", "Eating during specific hours and fasting the rest of the day."],
  ["Are supplements necessary?", "Not always—healthy food is usually enough unless advised by a doctor."],
  ["How do I stick to a meal plan?", "Plan ahead, keep meals simple, and stay consistent."],
];

const HEALTH_ROWS = [
  ["What is health insurance?", "It is a plan that helps pay for medical expenses."],
  ["Why do I need health insurance?", "It reduces the cost of treatment when you are sick."],
  ["What is a medical check-up?", "A routine visit to a doctor to check your health."],
  ["How often should I visit a doctor?", "At least once a year or when you feel unwell."],
  ["What is high blood pressure?", "A condition where blood flows too strongly through your veins."],
  ["What causes diabetes?", "High sugar levels in the blood, often linked to diet and lifestyle."],
  ["How can I stay healthy daily?", "Eat well, exercise, sleep enough, and drink water."],
  ["What is mental health?", "Your emotional and psychological well-being."],
  ["When should I see a doctor for stress?", "When stress affects your sleep, mood, or daily life."],
  ["What are common signs of illness?", "Fever, pain, tiredness, and unusual body changes."],
  ["What is a pharmacy?", "A place where you buy medicines."],
  ["Can I take medicine without prescription?", "Some medicines are allowed, but others need a doctor’s advice."],
  ["What is a balanced lifestyle?", "A mix of healthy eating, exercise, rest, and stress control."],
  ["What is reproductive health?", "Health related to the reproductive system and sexual well-being."],
  ["How can I prevent diseases?", "Eat healthy, exercise, vaccinate, and maintain hygiene."],
  ["What is a hospital?", "A place where medical treatment is provided."],
  ["What is health screening?", "Tests done to detect diseases early."],
  ["What are common lifestyle diseases?", "Diseases like diabetes, hypertension, and obesity."],
  ["What is obesity?", "Excess body weight that affects health."],
  ["How can I lose weight safely?", "Eat healthy and exercise regularly."],
  ["What is a vaccine?", "A substance that protects you from certain diseases."],
  ["Are vaccines safe?", "Yes, they are tested and approved for safety."],
  ["What is first aid?", "Immediate help given before professional medical care."],
  ["What should I do in an emergency?", "Call for help and go to the nearest hospital."],
  ["What is sleep hygiene?", "Healthy habits that help you sleep better."],
  ["How many hours should I sleep?", "Adults need about 7–8 hours daily."],
  ["What causes headaches?", "Stress, dehydration, lack of sleep, or illness."],
  ["What is a healthy heart?", "A heart that pumps blood properly without disease."],
  ["How can I improve my immunity?", "Eat well, exercise, and get enough rest."],
  ["When should I go for a lab test?", "When advised by a doctor or for routine checks."],
];

const CAREERS_ROWS = [
  ["How do I find a job?", "Search online, ask people you know, and apply to companies directly."],
  ["What is a CV?", "A document that shows your skills, education, and work experience."],
  ["How do I write a good CV?", "Keep it simple, list your skills clearly, and include your experience."],
  ["What is a cover letter?", "A short letter explaining why you are the right person for a job."],
  ["How do I prepare for an interview?", "Learn about the company, practice answers, and dress properly."],
  ["What should I say in an interview?", "Be honest, confident, and explain your skills clearly."],
  ["How can I get a job without experience?", "Start with internships, volunteer work, or entry-level jobs."],
  ["What are remote jobs?", "Jobs you can do from home using the internet."],
  ["How do I get a job abroad?", "Apply through agencies, job sites, or company sponsorship programs."],
  ["What skills are in demand?", "Digital skills, communication, problem-solving, and technical skills."],
  ["What is freelancing?", "Working for yourself and offering services to different clients."],
  ["How do I start freelancing?", "Choose a skill, create a profile online, and start applying for jobs."],
  ["What is career growth?", "Improving your skills and moving to better job positions."],
  ["How do I change careers?", "Learn new skills and apply for jobs in your new field."],
  ["What is an internship?", "A short-term job to gain experience."],
  ["Are internships paid?", "Some are paid, others are not."],
  ["What is a job offer?", "An official invitation from a company to work for them."],
  ["How do I negotiate salary?", "Research average pay and ask politely for better pay if needed."],
  ["What is workplace professionalism?", "Being respectful, responsible, and reliable at work."],
  ["How do I improve my skills?", "Take courses, practice, and learn from others."],
  ["What are soft skills?", "Skills like communication, teamwork, and leadership."],
  ["What are hard skills?", "Technical skills like coding, accounting, or design."],
  ["How do I build a career plan?", "Set goals, learn skills, and take steps toward your dream job."],
  ["What is entrepreneurship?", "Starting and running your own business."],
  ["How do I start a small business?", "Find an idea, plan it, and start small."],
  ["What is job security?", "The chance of keeping your job long-term."],
  ["How do I handle job rejection?", "Learn from it, improve, and keep applying."],
  ["What is networking?", "Meeting people who can help your career."],
  ["How do I grow in my current job?", "Work hard, learn new skills, and take initiative."],
  ["What is work-life balance?", "Balancing your job with your personal life."],
];

const BETTING_ROWS = [
  ["What is betting?", "Placing money on the outcome of a game or event."],
  ["Is betting legal?", "It depends on the country, but many places allow licensed betting."],
  ["How do I start betting?", "Register on a betting platform, deposit money, and place a bet."],
  ["What is a betting site?", "An online platform where you place bets."],
  ["What are odds?", "Numbers that show how much you can win from a bet."],
  ["How do odds work?", "Higher odds mean higher possible winnings but lower chances."],
  ["What is a single bet?", "A bet placed on one event."],
  ["What is a multiple bet?", "A bet placed on several events at once."],
  ["What is a jackpot bet?", "A large bet involving many matches for a big prize."],
  ["How do I win in betting?", "Study games, manage your money, and avoid risky bets."],
  ["Can I make money from betting?", "It is possible, but not guaranteed. It involves risk."],
  ["What is responsible gambling?", "Betting within your limits and avoiding addiction."],
  ["How do I avoid losing money?", "Set a budget and do not chase losses."],
  ["What is a bonus?", "Extra money or rewards given by betting platforms."],
  ["What is a free bet?", "A bet given by the platform without using your money."],
  ["How do I withdraw my winnings?", "Request withdrawal through the platform to your bank or mobile money."],
  ["How long does withdrawal take?", "Usually a few minutes to a few days."],
  ["What is a betting strategy?", "A plan you use to decide how and when to bet."],
  ["What is live betting?", "Betting while a game is in progress."],
  ["What are virtual games?", "Computer-generated games you can bet on anytime."],
  ["What is a sportsbook?", "A section of a betting platform for sports betting."],
  ["What is a casino game?", "Games like slots, blackjack, and roulette."],
  ["Can I bet on my phone?", "Yes, most platforms have mobile apps."],
  ["What is eSports betting?", "Betting on video game competitions."],
  ["What payment methods can I use?", "Mobile money, bank transfer, or cards."],
  ["What should I check before joining a betting site?", "Check if it is licensed and trusted."],
  ["What is addiction in betting?", "When someone cannot stop betting even when losing."],
  ["How do I control my betting habits?", "Set limits and take breaks."],
  ["What is a payout?", "The money you receive after winning a bet."],
  ["When should I stop betting?", "When you reach your limit or feel it is affecting your life."],
];

const topCat = (icon, faqs) => ({
  id: "top-30",
  label: "Top 30 FAQs",
  icon,
  faqs,
});

export const FAQ_INDUSTRIES = [
  {
    id: "real-estate-mortgages",
    label: "Real Estate & Mortgages",
    color: GOLD,
    icon: "Home",
    categories: [topCat("Home", pack(REAL_ESTATE_ROWS))],
  },
  {
    id: "legal",
    label: "Legal",
    color: GOLD,
    icon: "Scale",
    categories: [topCat("Scale", pack(LEGAL_ROWS))],
  },
  {
    id: "nutrition-meal-plans-recipes",
    label: "Nutrition, Meal Plans & Recipes",
    color: GOLD,
    icon: "UtensilsCrossed",
    categories: [topCat("UtensilsCrossed", pack(NUTRITION_ROWS))],
  },
  {
    id: "health",
    label: "Health",
    color: GOLD,
    icon: "HeartPulse",
    categories: [topCat("HeartPulse", pack(HEALTH_ROWS))],
  },
  {
    id: "careers",
    label: "Careers",
    color: GOLD,
    icon: "Briefcase",
    categories: [topCat("Briefcase", pack(CAREERS_ROWS))],
  },
  {
    id: "betting-gaming",
    label: "Betting & Gaming",
    color: GOLD,
    icon: "Gamepad2",
    categories: [topCat("Gamepad2", pack(BETTING_ROWS))],
  },
];

/**
 * Items (categories) for a topic — matches shape expected by FAQsPage sidebar/drawer.
 */
export function getFaqItemsByIndustry(industryId) {
  const ind = FAQ_INDUSTRIES.find((i) => i.id === industryId);
  if (!ind) return [];
  return ind.categories.map((c) => ({
    id: c.id,
    name: c.label,
    lucideIcon: c.icon,
    description: `${c.faqs.length} curated answers.`,
  }));
}

export const ALL_FAQS_FLAT = FAQ_INDUSTRIES.flatMap((ind) =>
  ind.categories.flatMap((cat) =>
    cat.faqs.map((faq) => ({
      ...faq,
      industryId: ind.id,
      industryLabel: ind.label,
      industryColor: ind.color,
      categoryId: cat.id,
      categoryLabel: cat.label,
    }))
  )
);

export const ALL_FAQS_BY_ITEM_KEY = new Map(
  FAQ_INDUSTRIES.flatMap((ind) =>
    ind.categories.map((cat) => [`${ind.id}::${String(cat.id)}`, cat.faqs])
  )
);

export const TOTAL_INFOBOXX_FAQS = ALL_FAQS_FLAT.length;
